import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import {
    useTracker,
    type useTrackWebsiteReferralClick,
} from '@archipro-website/tracker';
import { useProfessionalData } from '~/modules/professional/hook/use-professional-data';

const utmCampaignLandingProfessionalAtom = atomWithStorage<{
    professional: { id: number; title: string };
    utmCampaign: string;
    utmSource: string;
    utmMedium: string;
} | null>(
    'utm-campaign-landing-professional',
    null,
    createJSONStorage(() => sessionStorage),
);

/**
 * Return a function that tracks the `TargetedWebsite` event if the professional
 * of the clicked website link matches the professional from the ad that the user
 * clicked on.
 *
 * This should be called when the `Website` event is tracked.
 * @see https://archipro.atlassian.net/browse/CREATIVE-2800
 */
export function useTrackTargetedWebsite() {
    const tracker = useTracker();
    const utmCampaignData = useAtomValue(utmCampaignLandingProfessionalAtom);

    return useCallback<
        NonNullable<Parameters<typeof useTrackWebsiteReferralClick>[2]>
    >(
        (url, { itemID, professionalID, professionalTitle }) => {
            // Don't track if the user wasn't sent from an ad or if the
            // professional's website doesn't match the professional from the ad.
            if (
                !utmCampaignData ||
                utmCampaignData.professional.id !== professionalID
            ) {
                return;
            }
            tracker.log('TargetedWebsite', {
                url: new URL(window.location.href),
                label: professionalTitle,
                data: {
                    ExternalURL: url,
                    LibraryItemID: itemID,
                    ProfessionalID: professionalID,
                    ExtraData: {
                        utmCampaign: utmCampaignData.utmCampaign,
                        utmSource: utmCampaignData.utmSource,
                        utmMedium: utmCampaignData.utmMedium,
                    },
                },
            });
        },
        [tracker, utmCampaignData],
    );
}

/**
 * Hook to save the UTM campaign data and professional details to session
 * storage if the user lands on a Library Item detail page (professional,
 * product, project, article) from an ad campaign. Checks for `utm_campaign`
 * in the search params and the current URL path.
 *
 * This data is used to track the `TargetedWebsite` event when the user clicks
 * on a link to a professional's website, and the user came from an ad.
 *
 * This hook should only be called once in the root component.
 * @see {@link useTrackTargetedWebsite}
 */
export function useUtmCampaignProfessional() {
    const [searchParams] = useSearchParams();
    const setUtmCampaignData = useSetAtom(utmCampaignLandingProfessionalAtom);
    const professionalData = useProfessionalData();
    const utmData = useMemo(() => {
        const utmCampaign = searchParams.get('utm_campaign');
        const utmSource = searchParams.get('utm_source');
        const utmMedium = searchParams.get('utm_medium');
        return { utmCampaign, utmSource, utmMedium };
    }, [searchParams]);

    useEffect(() => {
        if (utmData.utmCampaign && professionalData) {
            setUtmCampaignData({
                professional: {
                    id: professionalData.professional.ID,
                    title: professionalData.professional.Title,
                },
                utmCampaign: utmData.utmCampaign,
                utmSource: utmData.utmSource ?? '',
                utmMedium: utmData.utmMedium ?? '',
            });
        }
    }, [utmData, professionalData, setUtmCampaignData]);
}
